import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Building2, Users, Calendar, Stethoscope, TrendingUp } from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get('/dashboard/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-manrope font-bold mb-2" data-testid="admin-dashboard-title">
            {user?.role === 'super_admin' ? 'Platform Overview' : 'Hospital Dashboard'}
          </h1>
          <p className="text-slate-600">Welcome back, {user?.full_name}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {user?.role === 'super_admin' && stats?.total_hospitals !== undefined && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Hospitals</CardDescription>
                  <Building2 className="h-4 w-4 text-slate-400" />
                </div>
                <CardTitle className="text-3xl">{stats.total_hospitals}</CardTitle>
              </CardHeader>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription>Total Doctors</CardDescription>
                <Stethoscope className="h-4 w-4 text-slate-400" />
              </div>
              <CardTitle className="text-3xl">{stats?.total_doctors || 0}</CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription>Total Patients</CardDescription>
                <Users className="h-4 w-4 text-slate-400" />
              </div>
              <CardTitle className="text-3xl">{stats?.total_patients || 0}</CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardDescription>Total Appointments</CardDescription>
                <Calendar className="h-4 w-4 text-slate-400" />
              </div>
              <CardTitle className="text-3xl">{stats?.total_appointments || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
              <CardDescription>Your platform at a glance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Active Subscriptions</span>
                  <span className="font-semibold">
                    {user?.role === 'super_admin' ? stats?.total_hospitals || 0 : '1'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Growth This Month</span>
                  <span className="font-semibold text-emerald-600 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    +12%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest system updates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5"></div>
                  <div>
                    <p className="font-medium">New appointment booked</p>
                    <p className="text-slate-500 text-xs">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5"></div>
                  <div>
                    <p className="font-medium">Doctor profile updated</p>
                    <p className="text-slate-500 text-xs">5 hours ago</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
